'use client';

import { SxProps, Theme } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import ConfidenceCardLayout from './ConfidenceCardLayout';
import DocumentConfidenceMeter from './DocumentConfidenceMeter';
import { documentNextStepGuidance } from './guidanceCopy';
import { documentConfidenceLevelName } from './ladder';
import { documentConfidenceStatement } from './statements';
import { TDocumentConfidenceLevel } from './types';

/**
 * The card's three states. See `BiometricConfidenceCard` for why this is a discriminated union with
 * a string discriminant rather than optional fields: a loading or failed card has no level and no
 * timestamp, and neither can be invented.
 */
type TDocumentConfidenceCardState =
  | {
      status?: 'ready';
      level: TDocumentConfidenceLevel;
      /** When the level shown was last determined. */
      lastUpdatedOn: string;
    }
  | { status: 'loading' }
  | { status: 'error' };

export type TDocumentConfidenceCardProps = TDocumentConfidenceCardState & {
  /**
   * A slot for buttons. The prototype gives this card a single filled `VIEW ASSESSMENT`, and only
   * at `Low`: that is the level that has triggered a rule and therefore has an assessment worth
   * reading. Omit it everywhere else, where nothing has been triggered and there is nothing to act on.
   */
  actions?: ReactNode;
  /** A slot for supporting imagery, e.g. a document evidence thumbnail. */
  media?: ReactNode;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
};

const HEADING = 'Identity document(s)';
const HELP_TEXT =
  'Reflects the combined outcome of the checks performed on these identity documents.';

/**
 * The identity-document check card: heading, meter, level name, statement and next-step guidance,
 * and a timestamp.
 *
 * There is no `title` or `helpText` prop here, unlike a generic card. This component owns its own
 * heading ("Identity document(s)") and tooltip copy, which is the whole reason it exists separately
 * from `BiometricConfidenceCard` rather than as one card taking an `axis` prop: two fixed headings
 * cannot be expressed as a single optional prop without a caller being able to get them wrong or
 * omit them.
 *
 * No `documentCount` prop either. The statement prose shifts plurality between `High` ("document
 * ... its") and `VeryHigh` ("documents ... their"), but that shift tracks the level, not how many
 * documents were checked, so there is nothing here for a count to control.
 *
 * **Only two levels carry next-step guidance.** `documentNextStepGuidance` is a
 * `Partial<Record<>>`, so `NotCompleted` and `Low` render `ConfidenceGuidance`'s tinted box and the
 * other five levels pass `undefined` through to `ConfidenceCardLayout`, which renders nothing for
 * them. The prototype populated this field on the document axis only; there is no biometric
 * equivalent.
 */
export default function DocumentConfidenceCard({
  actions,
  media,
  sx,
  'data-testid': dataTestId,
  ...state
}: Readonly<TDocumentConfidenceCardProps>): ReactElement {
  // Narrowing on `state.status` directly, for the reason given in `BiometricConfidenceCard`.
  if (state.status === 'loading' || state.status === 'error') {
    return (
      <ConfidenceCardLayout
        heading={HEADING}
        helpText={HELP_TEXT}
        status={state.status}
        actions={actions}
        media={media}
        sx={sx}
        data-testid={dataTestId}
      />
    );
  }

  return (
    <ConfidenceCardLayout
      heading={HEADING}
      helpText={HELP_TEXT}
      meter={<DocumentConfidenceMeter level={state.level} />}
      levelName={documentConfidenceLevelName(state.level)}
      statement={documentConfidenceStatement(state.level)}
      guidance={documentNextStepGuidance[state.level]}
      actions={actions}
      media={media}
      lastUpdatedOn={state.lastUpdatedOn}
      sx={sx}
      data-testid={dataTestId}
    />
  );
}
